import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { ScreenHeader } from "@/components/ScreenHeader";
import { MoveFilters, rangeToSince } from "@/components/MoveFilters";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { colors, spacing, fonts } from "@/theme";

function fmt(iso) {
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

export default function History() {
  const { user } = useAuth();
  const [moves, setMoves] = useState([]);
  const [yards, setYards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [range, setRange] = useState("all");
  const [yardFilter, setYardFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const data = await api("/moves?status=acknowledged");
      setMoves(data.filter((m) => m.acknowledged_by_id === user?.id));
    } catch (e) { console.error(e); }
  }, [user]);

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api("/locations");
        setYards(r.yards || []);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const since = rangeToSince(range);
    const q = search.trim().toLowerCase();
    return moves.filter((m) => {
      if (yardFilter !== "all" && m.yard !== yardFilter) return false;
      if (since && m.acknowledged_at) {
        if (new Date(m.acknowledged_at) < since) return false;
      }
      if (q) {
        const hay = `${m.trailer_number} ${m.from_location} ${m.to_location}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [moves, yardFilter, range, search]);

  return (
    <div style={{ flex: 1, backgroundColor: colors.surface }}>
      <ScreenHeader title="My History" subtitle={`${filtered.length} of ${moves.length} completed`} />
      <MoveFilters search={search} onSearchChange={setSearch} range={range} onRangeChange={setRange} placeholder="Search trailer or location" />

      {yards.length > 0 && (
        <div style={s.yardRow}>
          <YardChip label="All yards" count={moves.length} active={yardFilter === "all"} onClick={() => setYardFilter("all")} testId="hist-yard-all" />
          {yards.map((y) => (
            <YardChip key={y} label={y} count={moves.filter((m) => m.yard === y).length} active={yardFilter === y} onClick={() => setYardFilter(y)} testId={`hist-yard-${y}`} />
          ))}
        </div>
      )}

      {loading ? (
        <div style={s.spinnerWrap}><div className="yo-spinner" /></div>
      ) : filtered.length === 0 ? (
        <div style={s.empty}>{moves.length === 0 ? "No completed moves yet." : "No moves match your filters."}</div>
      ) : (
        <div style={s.list}>
          {filtered.map((item) => (
            <div key={item.id} data-testid={`hist-${item.id}`} style={s.row}>
              <CheckCircle2 size={20} color={colors.success} style={{ flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={s.trailerRow}>
                  <span style={s.trailer}>{item.trailer_number}</span>
                  {item.yard ? <span style={s.yardTag}>{item.yard}</span> : null}
                </div>
                <div style={s.route}>{item.from_location} → {item.to_location}</div>
                <div style={s.meta}>{item.acknowledged_at ? fmt(item.acknowledged_at) : ""}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function YardChip({ label, count, active, onClick, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} style={{ ...s.yardChip, ...(active ? s.yardChipActive : {}) }}>
      <span style={{ ...s.yardText, color: active ? colors.onBrandPrimary : colors.onSurfaceTertiary }}>{label}</span>
      <span style={{ ...s.badge, ...(active ? s.badgeActive : {}) }}>
        <span style={{ ...s.badgeText, color: active ? colors.brandPrimary : colors.onSurfaceTertiary }}>{count}</span>
      </span>
    </button>
  );
}

const s = {
  yardRow: { display: "flex", flexWrap: "wrap", gap: spacing.sm, padding: `${spacing.sm}px ${spacing.xl}px 0` },
  yardChip: { display: "flex", alignItems: "center", gap: spacing.xs, padding: `8px ${spacing.md}px`, borderWidth: 2, borderStyle: "solid", borderColor: colors.borderStrong, backgroundColor: colors.surfaceSecondary, cursor: "pointer" },
  yardChipActive: { backgroundColor: colors.brandPrimary, borderColor: colors.brandPrimary },
  yardText: { ...fonts.monoBold, fontSize: 10, letterSpacing: 0 },
  badge: { minWidth: 22, padding: "1px 6px", borderWidth: 1, borderStyle: "solid", borderColor: colors.borderStrong, backgroundColor: colors.surfaceTertiary, textAlign: "center" },
  badgeActive: { backgroundColor: colors.onBrandPrimary, borderColor: colors.onBrandPrimary },
  badgeText: { ...fonts.monoBold, fontSize: 10, letterSpacing: 0 },
  spinnerWrap: { display: "flex", justifyContent: "center", marginTop: 40 },
  empty: { textAlign: "center", marginTop: 48, ...fonts.monoBold, color: colors.onSurfaceTertiary },
  list: { display: "flex", flexDirection: "column", gap: spacing.sm, padding: spacing.xl, maxWidth: 720 },
  row: { display: "flex", alignItems: "center", gap: spacing.md, borderWidth: 2, borderStyle: "solid", borderColor: colors.border, backgroundColor: colors.surfaceSecondary, padding: spacing.md },
  trailerRow: { display: "flex", alignItems: "center", gap: spacing.sm },
  trailer: { ...fonts.monoBold, fontSize: 16, color: colors.onSurface },
  yardTag: { ...fonts.monoBold, fontSize: 10, color: colors.brandPrimary },
  route: { ...fonts.mono, fontSize: 12, color: colors.onSurfaceTertiary, marginTop: 2 },
  meta: { ...fonts.mono, fontSize: 11, color: colors.onSurfaceTertiary, marginTop: 2 },
};
